import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Star, ArrowRight } from 'lucide-react';
import { Nav, Footer } from './LandingPage';
import { api, getImageUrl } from '../utils/api';

const DISPLAY = 'font-display font-extrabold tracking-tighter';
const BODY = 'font-body';

const Stars = ({ rating, size = 'w-4 h-4' }) => (
  <div className="flex gap-0.5">
    {[1, 2, 3, 4, 5].map(i => (
      <Star key={i} className={`${size} ${i <= rating ? 'fill-amber-400 text-amber-400' : 'text-gray-300'}`} />
    ))}
  </div>
);

export default function Reviews() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.getPublicReviews().then(setData).catch(() => setData({ reviews: [], count: 0, average: 0 }));
  }, []);

  const schema = data?.count > 0 ? {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: 'LinkMonkey',
    url: 'https://linkmonkey.io/',
    description: 'LinkMonkey is an all-in-one link-in-bio tool and affordable Linktree alternative for creators.',
    brand: { '@type': 'Brand', name: 'LinkMonkey' },
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: data.average,
      reviewCount: data.count,
      bestRating: 5,
      worstRating: 1,
    },
    review: data.reviews.slice(0, 10).map(r => ({
      '@type': 'Review',
      author: { '@type': 'Person', name: r.name || r.username },
      reviewRating: { '@type': 'Rating', ratingValue: r.rating, bestRating: 5 },
      reviewBody: r.text,
      datePublished: (r.created_at || '').slice(0, 10),
    })),
  } : null;

  return (
    <div className="min-h-screen bg-[#D9E9FC] text-gray-950 antialiased selection:bg-gray-950 selection:text-white" data-testid="reviews-page">
      <Helmet>
        <title>LinkMonkey Reviews — What Creators Say About Our Link in Bio Tool</title>
        <meta name="description" content="Real reviews from LinkMonkey users. See what creators, musicians and businesses say about the free link-in-bio tool with rich embeds, tip jar and analytics." />
        <link rel="canonical" href="https://linkmonkey.io/reviews" />
        {schema && <script type="application/ld+json">{JSON.stringify(schema)}</script>}
        <link rel="preconnect" href="https://api.fontshare.com" crossOrigin="anonymous" />
        <link href="https://api.fontshare.com/v2/css?f[]=cabinet-grotesk@400,500,700,800,900,400i&f[]=satoshi@400,500,700&display=swap" rel="stylesheet" />
      </Helmet>
      <Nav />

      <header className="pt-32 pb-12 px-6 md:px-10 max-w-5xl mx-auto text-center">
        <h1 className={`${DISPLAY} text-4xl sm:text-5xl lg:text-6xl mb-4`}>LinkMonkey Reviews</h1>
        <p className={`${BODY} text-base md:text-lg text-gray-600 max-w-2xl mx-auto`}>
          Honest words from the creators, musicians and businesses using LinkMonkey every day.
        </p>
        {data?.count > 0 && (
          <div className="mt-6 inline-flex items-center gap-3 bg-white rounded-full px-6 py-3 shadow-sm" data-testid="reviews-aggregate">
            <span className={`${DISPLAY} text-3xl`}>{data.average}</span>
            <Stars rating={Math.round(data.average)} size="w-5 h-5" />
            <span className={`${BODY} text-sm text-gray-500`}>from {data.count} review{data.count === 1 ? '' : 's'}</span>
          </div>
        )}
      </header>

      <main className="px-6 md:px-10 max-w-5xl mx-auto pb-24">
        {!data ? (
          <p className={`${BODY} text-center text-gray-500 py-16`}>Loading reviews…</p>
        ) : data.reviews.length === 0 ? (
          <div className="text-center py-16 bg-white/60 rounded-3xl" data-testid="reviews-empty">
            <Stars rating={5} size="w-6 h-6" />
            <p className={`${DISPLAY} text-2xl mt-4 mb-2`}>Be the first to review LinkMonkey</p>
            <p className={`${BODY} text-gray-600 mb-6`}>Using LinkMonkey? Leave a review from your dashboard — we'd love to hear from you.</p>
            <Link to="/signup" className="inline-flex items-center gap-2 bg-[#0055FF] text-white rounded-full px-6 py-3 font-semibold hover:bg-[#3377FF] transition-colors" data-testid="reviews-cta">
              Create your free page <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="reviews-grid">
            {data.reviews.map(r => (
              <article key={r.id} className="bg-white rounded-2xl p-6 shadow-sm border border-white/60 flex flex-col" data-testid={`review-card-${r.username}`}>
                <Stars rating={r.rating} />
                <p className={`${BODY} text-sm text-gray-700 leading-relaxed mt-3 flex-1`}>"{r.text}"</p>
                <div className="flex items-center gap-3 mt-5 pt-4 border-t border-gray-100">
                  {r.profilePicture ? (
                    <img src={getImageUrl(r.profilePicture)} alt={r.name} className="w-9 h-9 rounded-full object-cover" />
                  ) : (
                    <div className="w-9 h-9 rounded-full bg-[#0055FF] text-white flex items-center justify-center text-sm font-bold">
                      {(r.name || r.username || '?')[0].toUpperCase()}
                    </div>
                  )}
                  <div className="min-w-0">
                    <p className={`${BODY} text-sm font-semibold truncate`}>{r.name || r.username}</p>
                    <a href={`/${r.username}`} className={`${BODY} text-xs text-gray-500 hover:text-[#0055FF] transition-colors`}>@{r.username}</a>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}

        <div className="mt-16 text-center">
          <p className={`${DISPLAY} text-2xl md:text-3xl mb-3`}>Ready to try it yourself?</p>
          <p className={`${BODY} text-gray-600 mb-6`}>Free forever. Your page is live in about two minutes.</p>
          <Link to="/signup" className="inline-flex items-center gap-2 bg-[#0055FF] text-white rounded-full px-8 py-4 font-semibold hover:bg-[#3377FF] transition-colors shadow-[0_10px_30px_rgba(0,85,255,0.35)]" data-testid="reviews-bottom-cta">
            Create your free page <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </main>

      <Footer />
    </div>
  );
}
